import { Component, inject, signal, computed, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { PilotageService, PilotageEstablishment } from '../../core/services/pilotage.service';
import { SessionService } from '../../core/session.service';
import { formatInTimeZone } from 'date-fns-tz';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

type SiteStatus  = 'fluide' | 'tension' | 'critique' | 'no-data';
type DataSource  = 'sensor' | 'manual' | 'baseline-wizard' | 'baseline' | 'no-data';
type ViewMode    = 'grid' | 'list';

@Component({
  selector: 'app-pilotage',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe],
  templateUrl: './pilotage.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pilotage.component.scss'],
})
export class PilotageComponent implements OnInit, OnDestroy {
  private readonly service    = inject(PilotageService);
  private readonly session    = inject(SessionService);
  private readonly router     = inject(Router);
  private readonly translate  = inject(TranslateService);

  establishments = signal<PilotageEstablishment[]>([]);
  loading        = signal(true);
  error          = signal(false);

  activeFilter   = signal<SiteStatus | null>(null);
  manualViewMode = signal<ViewMode | null>(null);
  searchQuery    = signal('');
  searchOpen     = signal(false);

  // Tick chaque minute — alimente currentDate, timelineSlots, frameAgeLabel
  private readonly tick = signal(Date.now());

  private interval?: ReturnType<typeof setInterval>;

  // ─── Computed ───────────────────────────────────────────────────────────────

  private static readonly WEEKDAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  readonly currentDate = computed(() => {
    const now = new Date(this.tick());
    const weekday = this.translate.instant('common.days.longByCode.' + PilotageComponent.WEEKDAY_CODES[now.getDay()]);
    const month = this.translate.instant('common.months.longByIndex.' + now.getMonth());
    const date = `${weekday} ${now.getDate()} ${month} ${now.getFullYear()}`;
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return `${date} · ${time}`;
  });

  readonly timelineSlots = computed((): string[] => {
    const now   = new Date(this.tick());
    const slots: string[] = [];
    for (let i = 4; i >= 0; i--) {
      const t = new Date(now.getTime() - i * 15 * 60_000);
      const m = Math.floor(t.getMinutes() / 15) * 15;
      slots.push(`${String(t.getHours()).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
    return slots;
  });

  readonly fluidCount    = computed(() => this.establishments().filter(e => e.isOpen && this.getStatus(e) === 'fluide').length);
  readonly tensionCount  = computed(() => this.establishments().filter(e => e.isOpen && this.getStatus(e) === 'tension').length);
  readonly critiqueCount = computed(() => this.establishments().filter(e => e.isOpen && this.getStatus(e) === 'critique').length);

  readonly autoViewMode = computed<ViewMode>(() => 'grid');

  readonly viewMode = computed<ViewMode>(() =>
    this.manualViewMode() ?? this.autoViewMode()
  );

  readonly filteredEstablishments = computed(() => {
    const f = this.activeFilter();
    return f ? this.establishments().filter(e => e.isOpen && this.getStatus(e) === f) : this.establishments();
  });

  readonly sortedEstablishments = computed(() => {
    const order: Record<SiteStatus, number> = { critique: 0, tension: 1, fluide: 2, 'no-data': 3 };
    return [...this.filteredEstablishments()].sort((a, b) => {
      const openDiff = (a.isOpen ? 0 : 1) - (b.isOpen ? 0 : 1);
      if (openDiff !== 0) return openDiff;
      return order[this.getStatus(a)] - order[this.getStatus(b)];
    });
  });

  readonly showSearch = computed(() => this.establishments().length > 5);

  // Seuils affichés dans la légende — identiques pour tous les sites en pratique
  // (valeurs par défaut capteur non personnalisables, cf. PilotageController côté backend),
  // mais lus depuis les données pour rester exacts si un site a un jour des seuils propres.
  readonly legendThresholds = computed(() => {
    const first = this.establishments()[0];
    return {
      low: first?.thresholdLow ?? 10,
      high: first?.thresholdHigh ?? 20,
    };
  });

  readonly displayedEstablishments = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return this.sortedEstablishments();
    return this.sortedEstablishments().filter(e => e.name.toLowerCase().includes(q));
  });

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit() {
    this.loadData();
    // Refresh toutes les 5 min : tick + données
    this.interval = setInterval(() => {
      this.tick.set(Date.now());
      this.loadData();
    }, 5 * 60_000);
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }

  private loadData() {
    // Establishments en priorité — affiche la page dès que possible
    this.service.getEstablishments().subscribe({
      next: establishments => {
        this.establishments.set(establishments);
        this.loading.set(false);
      },
      error: () => { this.error.set(true); this.loading.set(false); },
    });

  }

  // ─── Status & display ───────────────────────────────────────────────────────

  trackById(_: number, e: PilotageEstablishment) { return e.establishmentId; }

  setViewMode(mode: ViewMode) {
    this.manualViewMode.set(mode);
  }

  closeSearch() {
    this.searchOpen.set(false);
    this.searchQuery.set('');
  }

  hasData(e: PilotageEstablishment): boolean {
    return e.sensorCount > 0;
  }

  isBaseline(e: PilotageEstablishment): boolean {
    return (e.waitSource === 'BASELINE' || e.waitSource === 'BASELINE_WIZARD' || e.waitSource === 'AUTO')
      && e.waitMinutes != null;
  }

  getDataSource(e: PilotageEstablishment): DataSource {
    if (this.isManual(e))                      return 'manual';
    // Provisoire : badge + entrées/sorties dès qu'un capteur est présent (sensorCount > 0),
    // sans attendre la validation de l'historique (waitSource === 'SENSOR' côté backend,
    // qui exige ≥5 occurrences du créneau) — à revoir une fois l'historique reconstitué.
    if (this.hasData(e))                       return 'sensor';
    if (e.waitSource === 'BASELINE_WIZARD')    return 'baseline-wizard';
    if (e.waitSource === 'BASELINE' || e.waitSource === 'AUTO') return 'baseline';
    return 'no-data';
  }

  getSourceLabel(e: PilotageEstablishment): string {
    switch (this.getDataSource(e)) {
      case 'sensor':           return this.translate.instant('pilotage.pilotage.source.sensor');
      case 'manual':           return this.translate.instant('pilotage.pilotage.source.temporary');
      case 'baseline-wizard':  return this.translate.instant('pilotage.pilotage.source.forecast');
      case 'baseline':         return this.translate.instant('pilotage.pilotage.source.theoretical');
      default:                 return '';
    }
  }

  getSourceTitle(e: PilotageEstablishment): string {
    switch (this.getDataSource(e)) {
      case 'sensor':          return this.translate.instant('pilotage.pilotage.source.sensorTitle');
      case 'manual':          return this.translate.instant('pilotage.pilotage.source.temporaryTitle');
      case 'baseline-wizard': return this.translate.instant('pilotage.pilotage.source.forecastTitle');
      case 'baseline':        return this.translate.instant('pilotage.pilotage.source.theoreticalTitle');
      default:                return this.translate.instant('pilotage.pilotage.source.noneTitle');
    }
  }

  statusLabel(status: SiteStatus): string {
    switch (status) {
      case 'fluide':   return this.translate.instant('pilotage.pilotage.status.fluid');
      case 'tension':  return this.translate.instant('pilotage.pilotage.status.moderate');
      case 'critique': return this.translate.instant('pilotage.pilotage.status.high');
      default:         return '';
    }
  }

  getStatus(e: PilotageEstablishment): SiteStatus {
    const mins = e.waitMinutes;
    if (mins != null) {
      if (mins > e.thresholdHigh) return 'critique';
      if (mins > e.thresholdLow)  return 'tension';
      return 'fluide';
    }
    return 'no-data';
  }

  /** Plancher d'affichage : jamais moins de 5 min affichées sur les cards pilotage */
  private clampWaitMinutes(mins: number | null | undefined): number | null {
    return mins == null ? null : Math.max(5, mins);
  }

  getWaitTime(e: PilotageEstablishment): number | null {
    return this.clampWaitMinutes(e.waitMinutes);
  }

  getBaselineWaitMinutes(e: PilotageEstablishment): number | null {
    return this.clampWaitMinutes(e.waitMinutes);
  }

  toggleFilter(status: SiteStatus) {
    this.activeFilter.set(this.activeFilter() === status ? null : status);
  }

  getManualWaitMinutes(e: PilotageEstablishment): number | null {
    if (!this.isManual(e) || e.waitMinutes == null) return null;
    return this.clampWaitMinutes(e.waitMinutes); // déclin géré côté backend via getNow()
  }

  isManual(e: PilotageEstablishment): boolean {
    if (e.waitSource !== 'MANUAL' || e.waitMinutes == null) return false;
    if (e.waitExpiresAt && new Date(e.waitExpiresAt) <= new Date()) return false;
    return true;
  }

  getTimelineSlots(e: PilotageEstablishment): string[] {
    const base = e.timelineBaseTime ? new Date(e.timelineBaseTime) : new Date(this.tick());
    const tz = e.timezone || 'Europe/Paris';
    const slots: string[] = [];
    for (let i = 4; i >= 0; i--) {
      const t = new Date(base.getTime() - i * 15 * 60_000);
      const h = parseInt(formatInTimeZone(t, tz, 'HH'), 10);
      const m = Math.floor(parseInt(formatInTimeZone(t, tz, 'mm'), 10) / 15) * 15;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
    return slots;
  }

  goToEtablissement(e: PilotageEstablishment): void {
    this.session.selectEstablishment(e.establishmentId);
    this.router.navigate(['/site']);
  }

  getTimelineDotColor(e: PilotageEstablishment, index: number): string {
    const LAST = this.timelineSlots().length - 1;

    // Slot courant : toujours waitMinutes vs seuils configurés
    if (index === LAST) {
      const st = this.getStatus(e);
      if (st === 'fluide')   return 'green';
      if (st === 'tension')  return 'orange';
      if (st === 'critique') return 'red';
      return 'inactive';
    }

    // Slots historiques : statuts envoyés par le backend
    const s = e.timelineStatuses?.[index];
    if (!s || s === 'NO_DATA' || s === 'CLOSED') return 'inactive';
    if (s === 'FLUIDE')   return 'green';
    if (s === 'TENSION')  return 'orange';
    if (s === 'CRITIQUE') return 'red';
    return 'inactive';
  }

  getTimelineMinutes(e: PilotageEstablishment, index: number): number | null {
    return this.clampWaitMinutes(e.timelineMinutes?.[index]);
  }
}
